import { Fragment, useEffect, useState } from 'react';

import type { HtmlSerializerProps } from '../../utils/html-serializer';
import htmlSerializer from '../../utils/html-serializer';

import type { BrazeCustomEvent } from './prescreen-consumer';

interface MetaLanguageMultiPageProps {
  content: HtmlSerializerProps['content'][][];
  fallback: HtmlSerializerProps['content'][];
  popupRef?: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onButtonClick?: (buttonId: string) => void;
  sendBrazeCustomEvent: (payload: BrazeCustomEvent) => void;
  onURLButtonClick: (
    url: string,
    target?: '_self' | '_blank',
    buttonId?: string
  ) => void;
}

export const MetaLanguageMultiPage: React.FC<MetaLanguageMultiPageProps> = ({
  content: contentArray,
  fallback: fallbackContent,
  onClose,
  onButtonClick,
  popupRef,
  sendBrazeCustomEvent,
  onURLButtonClick,
}) => {
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [fallbackPage, setFallbackPage] = useState<boolean>(false);

  useEffect(() => {
    if (pageNumber < 1) return;
    requestAnimationFrame(() => {
      if (!popupRef?.current) return;
      const fElement = popupRef.current?.querySelector<HTMLElement>(
        'h1, span, a, button'
      );
      if (fElement) {
        fElement.setAttribute('tabindex', '-1');
        fElement.focus();
      }
    });
  }, [pageNumber, popupRef]);

  const handleNextPage = (): void => {
    if (pageNumber < contentArray.length - 1) {
      setPageNumber(pageNumber + 1);
    } else {
      onClose();
    }
  };

  const onFallback = (): void => {
    setFallbackPage(true);
  };

  return (
    <>
      {fallbackPage
        ? fallbackContent.map((content, index) => (
            <Fragment key={`multi-step-popup-fallback-${index}`}>
              {htmlSerializer({
                content,
                onClose,
                onButtonClick,
                sendBrazeCustomEvent,
                onURLButtonClick,
                handleNextPage,
                onFallback,
              })}
            </Fragment>
          ))
        : (contentArray?.[pageNumber] ?? []).map((content, index) => (
            <Fragment key={`multi-step-popup-${index}`}>
              {htmlSerializer({
                content,
                onClose,
                onButtonClick,
                sendBrazeCustomEvent,
                onURLButtonClick,
                handleNextPage,
                onFallback,
              })}
            </Fragment>
          ))}
    </>
  );
};
